import axios from 'axios'
import { API_HOST } from "../../../config/app-const";
import { Pagination } from "../../../config/query-rules";
import { setSubFilters } from "../../../config/query-rules";
import timeDelay from "../../../helpers/timeDelay";
import { getNextPageUrl, pagination } from "../../../helpers/store";
import Vue from "vue";
import i18n from "../../../i18n";

const state = {
  syntax: {
    isBusy: false,
    error: null
  },
  queryFilters: {
    isBusy: false,
    data: [],
    error: null
  }
}

/**
 * Detect if the current data should be set to active true or not.
 *
 * @param {Array} data The data array to check.
 * @param {string} name The name to search.
 * @returns {boolean} True or false.
*/
function shouldBeActive(data, name) {
  for (let i = 0, l = data.length; i < l; ++i) {
    if (data[i].type === name) {
      return true;
    } else if (data[i].sub_options) {
      const tmp = shouldBeActive(data[i].sub_options, name);
      if (tmp === true) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Set is_active state to false, to everything.
 *
 * @param {Array} data Current filter list.
 * @param {string} name The name to search for.
 * @param {boolean} value The value we want to set.
*/
function selectActiveStatus(data, name) {
  for (let i = 0, l = data.length; i < l; ++i) {
    if (data[i].hasOwnProperty("is_active")) {
      data[i].is_active = shouldBeActive([data[i]], name);
      if (data[i].sub_options && data[i].type !== name) {
        selectActiveStatus(data[i].sub_options, name);
      }
    }
  }
}

/**
 * Walk to disable anything above a certain level.
 *
 * @param {Array} data Current filter list.
 * @param {string} name The name to search for.
 * @param {boolean} invalid False at the beginnning, will auto-set to true when needed.
*/
function invalidSubStatus(data, name, invalid) {
  for (let i = 0, l = data.length; i < l; ++i) {
    if (data[i].type === name || invalid === true) {
      if (data[i].hasOwnProperty("is_active")) {
        data[i].is_active = false;
      }
      if (data[i].sub_options) {
        invalidSubStatus(data[i].sub_options, name, true);
      }
    } else if (data[i].sub_options) {
      invalidSubStatus(data[i].sub_options, name, invalid);
    }
  }
}

/**
 * Disable all active status previous set.
 *
 * @param {Array} data Current filter list.
 * @param {string} name The name to search for.
 */
function cancelActiveStatus(data, name) {
  for (let i = 0, l = data.length; i < l; ++i) {
    if (data[i].hasOwnProperty("is_active")) {
      data[i].is_active = false;
      if (data[i].sub_options) {
        cancelActiveStatus(data[i].sub_options, name);
      }
    }
  }
}

/**
 * Check if it's a "root" or not.
 *
 * @param {Array} data Current filter list.
 * @param {string} name The name to search for.
 * @returns {boolean} True if its a root, false otherwise.
 */
function isRoot(data, name) {
  for (let i = 0, l = data.length; i < l; ++i) {
    if (data[i].type === name) {
      return true;
    }
  }
  return false;
}

/**
 * Find a given filter by it's type.
 *
 * @param {Array} data The data to search in.
 * @param {string} name The name to search for.
 * @returns {Object | undefined} A filter found or undefined.
 */
function findFilter(data, name) {
  for (let i = 0, l = data.length; i < l; ++i) {
    if (data[i].type === name) {
      return data[i];
    }
    if (data[i].sub_options) {
      let tmp = findFilter(data[i].sub_options, name);
      if (tmp) {
        return tmp;
      }
    }
  }
  return undefined;
}

/**
 * Pretty-print category names.
 *
 * @param {string} name The type we want to process.
 * @returns {string} The resulting prettified string.
 */
 function prettyPrintLabel(name) {
  switch(name) {
    case "contentNChars":
      return "Content (first/last N characters)"
    case "contentNPercentage":
      return "Content (first/last N Percent)"
    case "contentNParagraph":
      return "Content (first/last N Paragraphs)"
    case "textCaseSensitive":
      return "Case sensitive";
    case "textCaseInsensitive":
      return "Case insensitive";
    case "textRegexCaseSensitive":
      return "Strict match case sensitive";
    case "textRegexCaseInsensitive":
      return "Strict match case insensitive";
    default:
      return name.charAt(0).toUpperCase() + name.slice(1);
  }
}

const mutations = {

  SET_FILTERS_BUSY(state, payload) {
    state.queryFilters.isBusy = payload
  },

  FETCH_QUERY_FILTERS_SUCCESS(state, payload = []) {
    state.queryFilters.data = payload
  },

  FETCH_QUERY_FILTERS_FAIL(state, payload) {
    state.queryFilters.error = payload;
    state.queryFilters.data = []
  },

  SET_FILTER_ACTIVE(state, {name, value}) {
    if (!isRoot(state.queryFilters.data, name) && value === false) {
      invalidSubStatus(state.queryFilters.data, name, false);
    } else if (value === false) {
      cancelActiveStatus(state.queryFilters.data);
    } else {
      selectActiveStatus(state.queryFilters.data, name, value);
    }
  },

  SET_SUB_FILTER_BUSY(state, {subFilterName, value}) {
    const target = findFilter(state.queryFilters.data, subFilterName);
    if(target) {
      target.isBusy = value
    }
  },

  SET_FILTER_QUERY(state, {subFilterName, query}) {
    const target = findFilter(state.queryFilters.data, subFilterName);
    if(target) {
      target.filters.name = query
    }
  },

  SET_FILTER_PARAM(state, {subFilterName, paramName, value}) {
    const target = findFilter(state.queryFilters.data, subFilterName);
    if(target) {
      target[paramName] = value
    }
  },

  SET_SUB_FILTER_DATA(state, {subFilterName, data, reset }) {
    const filter = findFilter(state.queryFilters.data, subFilterName);
    if(filter) {
      if(reset) {
        filter.sub_options = []
      }
      filter.sub_options = [...filter.sub_options, ...data]
    }
  },

  RESET_PAGINATION(state, subFilterName) {
    const target = findFilter(state.queryFilters.data, subFilterName);
    if(target) {
      target.pagination = new Pagination()
      target.isInitiated = false
    }
  },

  SET_PAGINATED_RESOURCE(state, {key, resourceData, pagination:responsePagination, reset = false}) {
    const target = findFilter(state.queryFilters.data, key);
    if(reset) {
      target.isInitiated = false
      target.sub_options = []
      target.pagination = {...pagination, per_page: 20}
    }
    resourceData.forEach(query => {
      // if reflect query has no label or value, set them
      if(!Reflect.has(query, 'label')) Reflect.set(query, 'label', prettyPrintLabel(query.name || query.type))
      if(!Reflect.has(query, 'value')) Reflect.set(query, 'value', '')
      target.sub_options.push({
        ...query,
        is_active: false,
        isInitiated: false,
        sub_options: [],
        isDraggable: !query.url
      })
    })
    target.pagination = { ...target.pagination, ...responsePagination }
    target.isInitiated = true
  },

  TEST_SYNTAX_BUSY(state, busy) {
    state.syntax.isBusy = busy;
  },

  TEST_SYNTAX_SUCCESS(state) {
    state.syntax.error = undefined;
  },

  TEST_SYNTAX_FAIL(state, payload) {
    state.syntax.error = payload;
  }
}

const actions = {

  getFilters({ commit, rootState }, {projectUid}) {

    const start = Date.now() // start timer

    return new Promise((resolve, reject) => {

      commit('SET_FILTERS_BUSY', true)
      let url = `${API_HOST}/analysis/${projectUid}/rules/available/filters`

      axios.defaults.headers.common['Authorization'] = `Token token=${rootState.user.user.data.accessToken}`;

      axios.get(url)
        .then(({data}) => {
          commit('FETCH_QUERY_FILTERS_SUCCESS', setSubFilters(data, projectUid))
          resolve()
        })
        .catch((e) => {
          commit('FETCH_QUERY_FILTERS_FAIL', e)
          Vue.notify({
            group: 'store_notifications',
            type: 'error',
            text: i18n.t('messages.request.error')});
          reject(e)
        })
        .finally(async () => {
          await timeDelay(start) // delay for loader
          commit("SET_FILTERS_BUSY", false)
        })
    })
  },

  getSubFilters({ commit, state, rootState }, { subFilterName, query = '' }) {
    return new Promise((resolve, reject) => {
      const subFilter = findFilter(state.queryFilters.data, subFilterName);

      if(!subFilter) return resolve('subFilter not found')
      if(subFilter.isBusy) return resolve('subFilter is busy')

      axios.defaults.headers.common['Authorization'] = `Token token=${rootState.user.user.data.accessToken}`;

      // deal with query ( search )
      if(Reflect.has(subFilter, 'filters') && subFilter.filters.name !== query) {
        commit('SET_FILTER_QUERY', {subFilterName, query})
        commit('SET_FILTER_PARAM', {subFilterName, paramName: 'sub_options', value: []})
        commit('RESET_PAGINATION', subFilterName)
      }

      const { pagination, filters = {}, url = '' }	= subFilter
      let { page, total_pages, per_page } = { ...pagination }
      if(page && page >= total_pages) return resolve('no more pages')

      const localPagination = { page, per_page }

      commit('SET_SUB_FILTER_BUSY', {subFilterName, value: true})

      axios.get(API_HOST + getNextPageUrl(url, localPagination, filters))
        .then(({data: { data : { data: resourceData, pagination }}}) => {

          // reset pagination response to snake_case format
          pagination = {
            page: pagination.currentPage,
            total_count: pagination.totalCount,
            total_pages: pagination.totalPages,
            per_page: pagination.perPage || per_page
          }

          commit("SET_PAGINATED_RESOURCE", { key: subFilterName, resourceData, pagination, reset : pagination.page === 1})
          resolve()
        })
        .catch((e) => reject(e))
        .finally(() => {
          commit('SET_FILTER_PARAM', {subFilterName, paramName: 'isInitiated', value: true})
          commit('SET_SUB_FILTER_BUSY', {subFilterName, value: false})
        })
    })
  },

  testSyntax({ commit, state, rootState }, { projectUid, search = '', caseSensitive = false }) {
    commit("TEST_SYNTAX_BUSY", true);

    axios.defaults.headers.common['Authorization'] = `Token token=${rootState.user.user.data.accessToken}`;

    const body = {
      search: search,
      case_sensitive: caseSensitive
    };

    return axios.post(
      `${API_HOST}/analysis/${projectUid}/rules/filters/syntax/test`, body
    )
      .then(response => {
        if (!response.data.error) {
          commit("TEST_SYNTAX_SUCCESS");
        } else {
          commit("TEST_SYNTAX_FAIL", response.data.error.message || "Unknow error");
        }
        return response.data
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("TEST_SYNTAX_FAIL", error);
      })
      .finally(() => commit("TEST_SYNTAX_BUSY", false));
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
};